import { AlertTriangle } from 'lucide-react';
import type { EnrollmentBillingOptions } from '../../lib/api/billing';
import { INTERVAL_LABEL, PERIOD_LABEL, formatCents } from '../../lib/billing';
import { cn } from '../../lib/utils';

interface Props {
  options: EnrollmentBillingOptions;
  interval?: number;
  onIntervalChange: (value: number) => void;
  selectionLabel: string;
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Anmelde-Wizard, Schritt „Pakete“ beim Beitragsmodell (ADR-0003): Tarif und Zahlweise statt
 * Paket-Informationen. Die Auswahl landet im Workflow der Anmeldung; das Vertragskonto
 * entsteht, sobald die Anmeldung angenommen oder der Kurs gestartet wird.
 */
export default function PeriodicTariffStep({ options, interval, onIntervalChange, selectionLabel }: Props) {
  const tariff = options.tariff;
  const intervals = options.allowedIntervals?.length ? options.allowedIntervals : [1];
  const pricePeriod = tariff?.pricePeriodMonths || 1;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm">
        Gewählt: <span className="text-foreground">{selectionLabel}</span>
      </div>

      <div className="rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground">Beitrag</div>
      {tariff ? (
        <div className="rounded-lg border p-4">
          <div className="font-medium">{tariff.name}</div>
          <div className="text-sm text-muted-foreground">
            {formatCents(tariff.priceCents)} je {PERIOD_LABEL[pricePeriod] ?? `${pricePeriod} Monate`}
          </div>
        </div>
      ) : (
        <div className="flex gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            Für diese Unterrichtsform ist kein Beitragstarif hinterlegt. Ohne Tarif entstehen keine Beiträge – bitte
            unter „Tarife“ einen anlegen.
          </span>
        </div>
      )}

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Zahlweise</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {intervals.map((value) => {
            const selected = interval === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={selected}
                onClick={() => onIntervalChange(value)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                  selected ? 'border-primary bg-primary/10' : 'hover:bg-muted/40',
                )}
              >
                <span className="block font-medium">{capitalize(INTERVAL_LABEL[value] ?? `${value} Monate`)}</span>
                {tariff && (
                  <span className="block tabular-nums text-muted-foreground">
                    {formatCents(Math.round((tariff.priceCents * value) / pricePeriod))}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </fieldset>

      <p className="text-sm text-muted-foreground">
        Der Beitrag ist im Voraus zum Beginn jedes Zeitraums fällig; der Monat des Kursbeginns zählt voll. Das
        Vertragskonto entsteht, sobald die Anmeldung angenommen oder der Kurs gestartet wird.
      </p>
    </div>
  );
}
